package com.islamicmedia.site.components

import androidx.compose.runtime.*
import com.islamicmedia.shared.data.api.serverBaseUrl
import com.islamicmedia.shared.data.auth.SupabaseAuthService
import com.islamicmedia.shared.data.storage.SettingsStorage
import com.islamicmedia.shared.platform.triggerLightHaptic
import com.islamicmedia.site.theme.*
import kotlinx.browser.window
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Job
import kotlinx.coroutines.async
import kotlinx.coroutines.await
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch
import kotlinx.coroutines.MainScope
import kotlinx.serialization.json.Json
import kotlinx.serialization.json.JsonObject
import kotlinx.serialization.json.JsonPrimitive
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*
import org.w3c.dom.HTMLDivElement

private data class DhikrPhrase(
    val arabic: String,
    val transliteration: String,
    val meaning: String,
)

private val phrases = listOf(
    DhikrPhrase("سُبْحَانَ اللّٰهِ", "SubhanAllah", "Glory be to God"),
    DhikrPhrase("الْحَمْدُ لِلّٰهِ", "Alhamdulillah", "All praise is due to God"),
    DhikrPhrase("اللّٰهُ أَكْبَرُ", "Allahu Akbar", "God is the Greatest"),
    DhikrPhrase("لَا إِلٰهَ إِلَّا اللّٰهُ", "La ilaha illallah", "There is no god but God"),
    DhikrPhrase("أَسْتَغْفِرُ اللّٰهَ", "Astaghfirullah", "I seek God's forgiveness"),
    DhikrPhrase("سُبْحَانَ اللّٰهِ وَبِحَمْدِهِ", "SubhanAllahi wa bihamdihi", "Glory and praise be to God"),
    DhikrPhrase("لَا حَوْلَ وَلَا قُوَّةَ إِلَّا بِاللّٰهِ", "La hawla wa la quwwata illa billah", "There is no power except with God"),
    DhikrPhrase("سُبْحَانَ اللّٰهِ الْعَظِيمِ", "SubhanAllahil Adheem", "Glory be to God the Magnificent"),
    DhikrPhrase("اللَّهُمَّ صَلِّ عَلَى مُحَمَّدٍ", "Allahumma salli ala Muhammad", "O God, send blessings upon Muhammad"),
    DhikrPhrase("حَسْبُنَا اللّٰهُ وَنِعْمَ الْوَكِيلُ", "HasbunAllahu wa ni'mal wakeel", "God is sufficient for us"),
)

@Composable
fun DhikrCounter() {
    val settings = remember { SettingsStorage() }
    val scope = rememberCoroutineScope()

    // Per-dhikr session counters — recomposes by replacing the array on increment.
    var perDhikrCounts by remember { mutableStateOf(IntArray(phrases.size)) }
    val total = perDhikrCounts.sum()
    var highScore by remember { mutableStateOf(settings.loadDhikrHighScore()) }
    var activeIndex by remember { mutableStateOf(0) }
    var pulseTrigger by remember { mutableStateOf(0) }
    val carouselRef = remember { mutableStateOf<HTMLDivElement?>(null) }

    // Pull latest high from server on mount, if signed in.
    LaunchedEffect(Unit) {
        val isAuthed = try { SupabaseAuthService.instance.isLoggedIn } catch (_: Exception) { false }
        if (isAuthed) {
            val remote = fetchHighScoreWeb()
            if (remote != null && remote > highScore) {
                highScore = remote
                settings.saveDhikrHighScore(remote)
            }
        }
    }

    // Debounced server sync when total beats high score.
    var syncJob by remember { mutableStateOf<Job?>(null) }
    LaunchedEffect(total) {
        if (total > highScore) {
            highScore = total
            settings.saveDhikrHighScore(total)
            syncJob?.cancel()
            syncJob = scope.launch {
                delay(2000)
                val isAuthed = try { SupabaseAuthService.instance.isLoggedIn } catch (_: Exception) { false }
                if (isAuthed) {
                    val confirmed = updateHighScoreWeb(total)
                    if (confirmed != null && confirmed > highScore) {
                        highScore = confirmed
                        settings.saveDhikrHighScore(confirmed)
                    }
                }
            }
        }
    }

    InjectDhikrKeyframes()

    Div(attrs = {
        style {
            width(100.percent)
            // Match the mobile self-bounded height — parent activity host is unbounded.
            height(420.px)
            // Opaque panel so the dhikr stays readable over the analyzing screen's cover-art
            // backdrop (the cards are near-transparent and the poster bled through).
            backgroundColor(Color.white)
            borderRadius(20.px)
            paddingTop(8.px)
            property("box-sizing", "border-box")
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            alignItems(AlignItems.Stretch)
            property("user-select", "none")
            property("-webkit-user-select", "none")
            property("-webkit-tap-highlight-color", "transparent")
        }
    }) {
        // Top stat row: Total + High, centered.
        Div(attrs = {
            style {
                display(DisplayStyle.Flex)
                alignItems(AlignItems.Center)
                justifyContent(JustifyContent.Center)
                gap(8.px)
                padding(0.px, 8.px)
                marginBottom(8.px)
            }
        }) {
            StatPillWeb(label = "Total", value = total, r = 14, g = 124, b = 123)
            StatPillWeb(label = "High", value = highScore, r = 200, g = 150, b = 46, prefix = "🏆 ")
        }

        // Carousel viewport — `position: relative` so the prev/next arrow buttons can
        // absolute-position over the left/right edges. The arrows are the only navigation
        // method on web (no swipe/drag) per the user's request.
        Div(attrs = {
            style {
                position(Position.Relative)
                property("flex", "1")
                property("min-width", "0")
                width(100.percent)
            }
        }) {
            // Prev arrow — disabled (faded) when on the first panel.
            CarouselArrow(
                direction = CarouselDirection.Prev,
                disabled = activeIndex == 0
            ) { scrollCarouselTo(carouselRef.value, activeIndex - 1, phrases.size) }

            // Next arrow — disabled when on the last panel.
            CarouselArrow(
                direction = CarouselDirection.Next,
                disabled = activeIndex == phrases.size - 1
            ) { scrollCarouselTo(carouselRef.value, activeIndex + 1, phrases.size) }

            // Horizontal carousel itself — panels are 78% wide; scroll-padding-inline reserves
            // the remaining 11% on each edge for the previous/next panels to peek in.
            Div(attrs = {
                ref { el ->
                    val div = el as HTMLDivElement
                    carouselRef.value = div
                    onDispose { carouselRef.value = null }
                }
                style {
                    display(DisplayStyle.Flex)
                    property("overflow-x", "auto")
                    property("scroll-snap-type", "x mandatory")
                    property("scroll-padding-inline", "11%")
                    property("scrollbar-width", "none")
                    property("-ms-overflow-style", "none")
                    width(100.percent)
                    height(100.percent)
                    // Critical for flex items that need to scroll their overflow content.
                    property("min-width", "0")
                    property("gap", "8px")
                    property("box-sizing", "border-box")
                }
                attr("class", "dhikr-carousel")
            }) {
            phrases.forEachIndexed { idx, p ->
                val count = perDhikrCounts[idx]
                val isActive = idx == activeIndex
                Div(attrs = {
                    onClick {
                        val updated = perDhikrCounts.copyOf()
                        updated[idx] = updated[idx] + 1
                        perDhikrCounts = updated
                        pulseTrigger++
                        triggerLightHaptic()
                    }
                    style {
                        property("flex", "0 0 78%")
                        property("scroll-snap-align", "center")
                        display(DisplayStyle.Flex)
                        flexDirection(FlexDirection.Column)
                        alignItems(AlignItems.Center)
                        justifyContent(JustifyContent.Center)
                        cursor("pointer")
                        padding(16.px, 18.px)
                        property("box-sizing", "border-box")
                        // Card styling — matches the mobile soft-card treatment so the panel
                        // bounds are visible regardless of how short the dhikr text is.
                        borderRadius(20.px)
                        property("background", "rgba(14, 124, 123, 0.04)")
                        property("border", "1px solid rgba(14, 124, 123, 0.15)")
                        property("box-sizing", "border-box")
                        // Faded peek for non-active neighbors — kept at 0.55 (same floor as
                        // mobile) so they're clearly visible at rest, not invisible.
                        opacity(if (isActive) 1.0 else 0.55)
                        property("transform", if (isActive) "scale(1)" else "scale(0.95)")
                        property("transition", "opacity 0.2s ease, transform 0.2s ease")
                    }
                }) {
                    // Arabic
                    Div(attrs = {
                        style {
                            fontSize(28.px)
                            color(Primary)
                            fontWeight(600)
                            property("font-family", "'Traditional Arabic', 'Amiri', 'Noto Sans Arabic', serif")
                            property("line-height", "1.6")
                            property("text-align", "center")
                        }
                    }) { Text(p.arabic) }
                    Div(attrs = { style { height(8.px) } }) {}
                    Div(attrs = {
                        style {
                            fontSize(18.px); color(Accent); fontWeight(500)
                            property("letter-spacing", "0.02em")
                        }
                    }) { Text(p.transliteration) }
                    Div(attrs = { style { height(4.px) } }) {}
                    Div(attrs = {
                        style {
                            fontSize(13.px); color(Gray500)
                            property("font-style", "italic")
                        }
                    }) { Text("\"${p.meaning}\"") }
                    Div(attrs = { style { height(28.px) } }) {}
                    // Big counter circle. key(pulseTrigger) on the active panel forces a remount
                    // every tap so the CSS animation re-fires from frame 0; non-active panels
                    // remain key-stable so they don't flicker when swiping past.
                    if (idx == activeIndex) {
                        key(pulseTrigger) {
                            CounterCircle(value = count, animate = true)
                        }
                    } else {
                        CounterCircle(value = count, animate = false)
                    }
                    Div(attrs = { style { height(12.px) } }) {}
                    Div(attrs = {
                        style { fontSize(11.px); color(Gray400) }
                    }) { Text("Tap anywhere") }
                }
            }
            } // close the inner carousel Div (overflow scroll container)
        } // close the outer wrapper Div (position: relative — holds the arrows)

        // Page indicator dots
        Div(attrs = {
            style {
                display(DisplayStyle.Flex)
                justifyContent(JustifyContent.Center)
                gap(6.px)
                marginTop(12.px)
            }
        }) {
            phrases.forEachIndexed { index, _ ->
                val active = index == activeIndex
                Div(attrs = {
                    onClick {
                        scrollCarouselTo(carouselRef.value, index, phrases.size)
                    }
                    style {
                        width(if (active) 8.px else 6.px)
                        height(if (active) 8.px else 6.px)
                        borderRadius(50.percent)
                        backgroundColor(if (active) Primary else Gray200)
                        cursor("pointer")
                        property("transition", "all 0.2s ease")
                    }
                }) {}
            }
        }
    }

    // Track scroll position to update activeIndex (so the dots highlight the visible panel).
    LaunchedEffect(carouselRef.value) {
        val el = carouselRef.value ?: return@LaunchedEffect
        // Poll scroll position every 100ms — cheap and avoids needing scroll listener bookkeeping.
        while (true) {
            delay(100)
            val w = el.clientWidth
            if (w > 0) {
                val newIndex = ((el.scrollLeft + w / 2.0) / w.toDouble()).toInt()
                    .coerceIn(0, phrases.size - 1)
                if (newIndex != activeIndex) activeIndex = newIndex
            }
        }
    }

}

/**
 * Helper: scroll the carousel to a specific dhikr panel by index. Uses scrollIntoView so
 * scroll-snap handles the alignment automatically; called from both the dot indicators
 * and the prev/next arrow buttons.
 */
private fun scrollCarouselTo(carousel: HTMLDivElement?, index: Int, total: Int) {
    val el = carousel ?: return
    val clamped = index.coerceIn(0, total - 1)
    if (clamped >= el.children.length) return
    val panel = el.children.item(clamped) as? org.w3c.dom.HTMLElement ?: return
    panel.scrollIntoView(
        kotlin.js.json(
            "behavior" to "smooth",
            "block" to "nearest",
            "inline" to "center"
        )
    )
}

private enum class CarouselDirection { Prev, Next }

/**
 * Floating left/right arrow button overlaid on the carousel viewport. Disabled state fades
 * the button so the user knows there's no further panel that direction. We position the
 * button absolutely INSIDE the carousel viewport (parent has `position: relative`) so it
 * stays anchored to the visible window even as the user paginates.
 */
@Composable
private fun CarouselArrow(
    direction: CarouselDirection,
    disabled: Boolean,
    onClick: () -> Unit
) {
    Div(attrs = {
        if (!disabled) onClick { onClick() }
        style {
            position(Position.Absolute)
            property("top", "50%")
            property("transform", "translateY(-50%)")
            if (direction == CarouselDirection.Prev) {
                property("left", "4px")
            } else {
                property("right", "4px")
            }
            width(36.px)
            height(36.px)
            borderRadius(18.px)
            backgroundColor(Color.white)
            property("box-shadow", "0 2px 8px rgba(0,0,0,0.10)")
            display(DisplayStyle.Flex)
            alignItems(AlignItems.Center)
            justifyContent(JustifyContent.Center)
            color(Primary)
            fontSize(20.px)
            fontWeight(300)
            property("z-index", "2")
            cursor(if (disabled) "default" else "pointer")
            opacity(if (disabled) 0.35 else 1.0)
            property("transition", "opacity 0.2s ease, transform 0.1s ease")
            property("user-select", "none")
        }
    }) {
        Text(if (direction == CarouselDirection.Prev) "\u2039" else "\u203A")
    }
}

/** Big circular counter rendered on each carousel panel. When [animate]=true the dhikrPulse
 *  keyframe plays from frame 0 (relies on key() at the call site to force a remount). */
@Composable
private fun CounterCircle(value: Int, animate: Boolean) {
    Div(attrs = {
        style {
            width(96.px); height(96.px)
            borderRadius(48.px)
            display(DisplayStyle.Flex)
            alignItems(AlignItems.Center)
            justifyContent(JustifyContent.Center)
            property("border", "2px solid ${Primary}")
            fontSize(28.px); fontWeight(600); color(Primary)
            if (animate) property("animation", "dhikrPulse 250ms ease-out")
        }
    }) { Text(value.toString()) }
}

/** One-shot pill matching the mobile StatPill — small rounded badge with label + value. */
@Composable
private fun StatPillWeb(label: String, value: Int, r: Int, g: Int, b: Int, prefix: String = "") {
    Div(attrs = {
        style {
            display(DisplayStyle.Flex)
            alignItems(AlignItems.Center)
            gap(6.px)
            padding(6.px, 10.px)
            borderRadius(9999.px)
            property("background", "rgba($r, $g, $b, 0.10)")
            fontSize(11.px)
        }
    }) {
        Span(attrs = {
            style { color(Gray700); fontWeight(500) }
        }) { Text("$prefix$label") }
        Span(attrs = {
            style { fontSize(13.px); fontWeight(700); color(rgb(r, g, b)) }
        }) { Text(value.toString()) }
    }
}

/** Inject the pulse keyframe + a small style block to hide the carousel's webkit scrollbar. */
@Composable
private fun InjectDhikrKeyframes() {
    LaunchedEffect(Unit) {
        val styleId = "dhikr-counter-styles"
        val doc = kotlinx.browser.document
        if (doc.getElementById(styleId) != null) return@LaunchedEffect
        val style = doc.createElement("style") as org.w3c.dom.HTMLStyleElement
        style.id = styleId
        // textContent (not innerHTML) — content is a static literal but the browser parses
        // CSS rules from <style>.textContent the same way.
        style.textContent = """
            @keyframes dhikrPulse {
                0%   { transform: scale(1); }
                50%  { transform: scale(1.15); }
                100% { transform: scale(1); }
            }
            .dhikr-carousel::-webkit-scrollbar { display: none; }
        """.trimIndent()
        doc.head?.appendChild(style)
    }
}

private suspend fun fetchHighScoreWeb(): Int? {
    return try {
        val token = try { SupabaseAuthService.instance.getFreshAccessToken() } catch (_: Exception) { null }
            ?: return null
        val baseUrl = serverBaseUrl()
        val headers = kotlin.js.json("Authorization" to "Bearer $token", "Accept" to "application/json")
        val response = window.fetch(
            "$baseUrl/api/account/dhikr",
            kotlin.js.json("method" to "GET", "headers" to headers).unsafeCast<org.w3c.fetch.RequestInit>()
        ).await()
        if (!response.ok) return null
        val text = response.text().await()
        val obj = Json.parseToJsonElement(text.toString()) as? JsonObject ?: return null
        (obj["high_score"] as? JsonPrimitive)?.content?.toIntOrNull()
    } catch (_: Throwable) { null }
}

private suspend fun updateHighScoreWeb(score: Int): Int? {
    return try {
        val token = try { SupabaseAuthService.instance.getFreshAccessToken() } catch (_: Exception) { null }
            ?: return null
        val baseUrl = serverBaseUrl()
        val headers = kotlin.js.json(
            "Authorization" to "Bearer $token",
            "Content-Type" to "application/json",
            "Accept" to "application/json"
        )
        val init = kotlin.js.json(
            "method" to "POST",
            "headers" to headers,
            "body" to """{"score":$score}"""
        ).unsafeCast<org.w3c.fetch.RequestInit>()
        val response = window.fetch("$baseUrl/api/account/dhikr", init).await()
        if (!response.ok) return null
        val text = response.text().await()
        val obj = Json.parseToJsonElement(text.toString()) as? JsonObject ?: return null
        (obj["high_score"] as? JsonPrimitive)?.content?.toIntOrNull()
    } catch (_: Throwable) { null }
}
